import { ClientType } from '@/shared/types/enum';
import { ModelNamePick } from '@/shared/types/model';
import { merge } from '@/shared/utils/object';

import { Account, CompanyView, IndividualView, Service, Task, TaskView, User } from '@prisma/client';

export type PlaceholderPrefix = 'Account' | 'User' | 'Company' | 'Individual' | 'Service' | 'Task';

export type PlaceholderModel = Account | User | CompanyView | IndividualView | Service | Task;

export type PlaceholderModelName = ModelNamePick<
  'Account' | 'User' | 'CompanyView' | 'IndividualView' | 'Service' | 'Task'
>;

export const PlaceholderPrefixModelMap: Record<PlaceholderPrefix, PlaceholderModelName> = {
  Account: 'Account',
  User: 'User',
  Company: 'CompanyView',
  Individual: 'IndividualView',
  Service: 'Service',
  Task: 'Task',
};

export const PlaceholderDateFormats = {
  DayMonthYear: 'dd/MM/yy',
  Day: 'd',
  DayOrdinal: 'do',
  DayName: 'EEEE',
  DayNameShort: 'EEE',
  Month: 'M',
  MonthName: 'MMMM',
  MonthNameShort: 'MMM',
  Year: 'yyyy',
};

const PlaceholderDateFormatType: keyof typeof PlaceholderDateFormats = 'DayMonthYear';

type Placeholder<T> = { name: string; column: keyof T; dateFormat?: string };

export const PLACEHOLDER_DELIMITERS: [string, string] = ['{{', '}}'];

export const PLACEHOLDER_REGEX = new RegExp(
  `(${PLACEHOLDER_DELIMITERS[0]}(${Object.keys(PlaceholderPrefixModelMap).join('|')})\\.([^}]+)${PLACEHOLDER_DELIMITERS[1]})`,
  'g',
);

export const PLACEHOLDER_RTE_REGEX = new RegExp(`<span[^>]*>${PLACEHOLDER_REGEX.source}</span>`, 'g');

const prefix = <T>(prefix: PlaceholderPrefix, placeholders: Placeholder<T>[]) => {
  return placeholders.map((placeholder) => ({ ...placeholder, name: `${prefix}.${placeholder.name}` }));
};

const datePlaceholders = <T>(base: Placeholder<T>) => [
  ...Object.keys(PlaceholderDateFormats).map((dateFormatType) => ({
    name: [base.name, ...(dateFormatType !== PlaceholderDateFormatType ? [dateFormatType] : [])].join('.'),
    column: base.column,
    dateFormat: PlaceholderDateFormats[dateFormatType as keyof typeof PlaceholderDateFormats],
  })),
];

const AccountPlaceholders: Placeholder<Account>[] = prefix('Account', [
  { name: 'Name', column: 'name' },
  { name: 'FromName', column: 'emailFromName' },
  { name: 'FromEmail', column: 'emailFromEmail' },
]);

const UserPlaceholders: Placeholder<User>[] = prefix('User', [
  { name: 'FirstName', column: 'firstName' },
  { name: 'LastName', column: 'lastName' },
  { name: 'FullName', column: 'name' },
  { name: 'Email', column: 'email' },
]);

const CompanyPlaceholders: Placeholder<CompanyView>[] = prefix('Company', [
  { name: 'Name', column: 'name' },
  { name: 'Type', column: 'companyType' },
  { name: 'RegistrationNumber', column: 'registrationNumber' },
  ...datePlaceholders<CompanyView>({ name: 'IncorporationDate', column: 'incorporationDate' }),
  // { name: 'YearEnd', column: 'yearEndDate' } ...datePlaceholders<>(),
  { name: 'AuthenticationCode', column: 'companiesHouseAuthenticationCode' },
  { name: 'UTR', column: 'utr' },
  { name: 'Reference', column: 'internalReference' },
  ...datePlaceholders<CompanyView>({ name: 'EngagementSigned', column: 'engagementSignedDate' }),
  ...datePlaceholders<CompanyView>({ name: 'AMLLastChecked', column: 'amlLastCheckedDate' }),
  // { name: 'AddressLine1', column: 'postalAddressLine_1' },
  // { name: 'AddressLine2', column: 'postalAddressLine_2' },
  // { name: 'AddressTown', column: 'postalAddressTown' },
  // { name: 'AddressCounty', column: 'postalAddressCounty' },
  // { name: 'AddressPostcode', column: 'postalAddressPostcode' },
  { name: 'PrimaryContactFirstName', column: 'principalContactFirstName' },
  { name: 'PrimaryContactLastName', column: 'principalContactLastName' },
  { name: 'PrimaryContactEmail', column: 'principalContactEmail' },
  { name: 'PrimaryContactPhone', column: 'contactTelephoneNumber' },
  ...datePlaceholders<CompanyView>({ name: 'AccountsLastMadeUpTo', column: 'accountsLastMadeUpToDate' }),
  ...datePlaceholders<CompanyView>({ name: 'AccountsPeriodEnd', column: 'accountsPeriodEndDate' }),
  ...datePlaceholders<CompanyView>({ name: 'AccountsDue', column: 'accountsDueDate' }),
  ...datePlaceholders<CompanyView>({
    name: 'ConfirmationStatementLastMadeUpTo',
    column: 'confirmationStatementLastMadeUpToDate',
  }),
  ...datePlaceholders<CompanyView>({
    name: 'ConfirmationStatementNextMadeUpTo',
    column: 'confirmationStatementNextMadeUpToDate',
  }),
  ...datePlaceholders<CompanyView>({ name: 'ConfirmationStatementDue', column: 'confirmationStatementDueDate' }),
  ...datePlaceholders<CompanyView>({ name: 'TaxReturnLastMadeUpTo', column: 'taxReturnLastMadeUpToDate' }),
  ...datePlaceholders<CompanyView>({ name: 'TaxPeriodEnd', column: 'taxPeriodEndDate' }),
  ...datePlaceholders<CompanyView>({ name: 'TaxReturnDue', column: 'taxReturnDueDate' }),
  ...datePlaceholders<CompanyView>({ name: 'TaxPaymentLastMadeUpTo', column: 'taxPaymentLastMadeUpToDate' }),
  ...datePlaceholders<CompanyView>({ name: 'TaxPaymentDue', column: 'taxPaymentDueDate' }),
  { name: 'VATNumber', column: 'vatHmrcRegistrationNumber' },
  ...datePlaceholders<CompanyView>({ name: 'VATRegistrationDate', column: 'vatRegistrationDate' }),
  ...datePlaceholders<CompanyView>({ name: 'VATDeregistrationDate', column: 'vatDeregistrationDate' }),
  // { name: 'VATQuarters', column: 'vatQuarters' },
  ...datePlaceholders<CompanyView>({ name: 'VATLastMadeUpTo', column: 'vatLastMadeUpToDate' }),
  ...datePlaceholders<CompanyView>({ name: 'VATPeriodEnd', column: 'vatPeriodEndDate' }),
  ...datePlaceholders<CompanyView>({ name: 'VATReturnDue', column: 'vatReturnDueDate' }),
  { name: 'PAYEReference', column: 'payeReference' },
  { name: 'PAYEAccountsOfficeReference', column: 'payeAccountsOfficeReferenceNumber' },
  // { name: 'PayrollFrequency', column: 'payrollFrequency' },
  // { name: 'PayrollDay', column: 'payrollDay' },
  ...datePlaceholders<CompanyView>({ name: 'PayrollLastMadeUpTo', column: 'payrollLastMadeUpToDate' }),
  ...datePlaceholders<CompanyView>({ name: 'PayrollDue', column: 'payrollDueDate' }),
  ...datePlaceholders<CompanyView>({ name: 'PayrollPaymentDue', column: 'payrollPaymentDueDate' }),
  ...datePlaceholders<CompanyView>({ name: 'CISLastMadeUpTo', column: 'cisLastMadeUpToDate' }),
  ...datePlaceholders<CompanyView>({ name: 'CISPeriodEnd', column: 'cisPeriodEndDate' }),
  ...datePlaceholders<CompanyView>({ name: 'CISReturnDue', column: 'cisReturnDueDate' }),
  // { name: 'BookkeepingFrequency', column: 'bookkeepingFrequency' },
  // { name: 'BookkeepingDay', column: 'bookkeepingDay' },
  ...datePlaceholders<CompanyView>({ name: 'BookkeepingLastMadeUpTo', column: 'bookkeepingLastMadeUpToDate' }),
  ...datePlaceholders<CompanyView>({ name: 'BookkeepingDue', column: 'bookkeepingDueDate' }),
]);

const IndividualPlaceholders: Placeholder<IndividualView>[] = prefix('Individual', [
  { name: 'FirstName', column: 'givenName' },
  { name: 'LastName', column: 'surname' },
  { name: 'FullName', column: 'name' },
  { name: 'UTR', column: 'utr' },
  { name: 'NI Number', column: 'nationalInsuranceNumber' },
  { name: 'Phone', column: 'contactTelephoneNumber' },
  { name: 'Email', column: 'emailAddress' },
  // { name: 'Address', column: 'address' },
  // { name: 'Postcode', column: 'postcode' },
  // { name: 'Reference', column: 'internalReference' },
  ...datePlaceholders<IndividualView>({ name: 'EngagementSigned', column: 'engagementSignedDate' }),
  ...datePlaceholders<IndividualView>({ name: 'AMLLastChecked', column: 'amlLastCheckedDate' }),
  ...datePlaceholders<IndividualView>({ name: 'TaxReturnLastMadeUpTo', column: 'taxReturnLastMadeUpToDate' }),
  ...datePlaceholders<IndividualView>({ name: 'TaxPeriodEnd', column: 'taxReturnPeriodEndDate' }),
  ...datePlaceholders<IndividualView>({ name: 'TaxReturnDue', column: 'taxReturnDueDate' }),
]);

const ServicePlaceholders: Placeholder<Service>[] = prefix('Service', [{ name: 'Name', column: 'name' }]);

const TaskPlaceholders: Placeholder<TaskView>[] = prefix('Task', [
  { name: 'Name', column: 'task' },
  ...datePlaceholders<TaskView>({ name: 'Due', column: 'deadlineDate' }),
  ...datePlaceholders<TaskView>({ name: 'PlannedStart', column: 'plannedStartDate' }),
  ...datePlaceholders<TaskView>({ name: 'PlannedCompletion', column: 'plannedCompletionDate' }),
  ...datePlaceholders<TaskView>({ name: 'Started', column: 'startedDate' }),
  ...datePlaceholders<TaskView>({ name: 'Completed', column: 'completedDate' }),
]);

const Placeholders: Placeholder<any>[] = [
  ...AccountPlaceholders,
  ...UserPlaceholders,
  ...CompanyPlaceholders,
  ...IndividualPlaceholders,
  ...ServicePlaceholders,
  ...TaskPlaceholders,
];

export const PlaceholderLookup: Record<string, Placeholder<any> | undefined> = Placeholders.map((placeholder) => ({
  [placeholder.name]: placeholder,
})).reduce(merge);

export const placeholdersForPrefixes = (prefixes: PlaceholderPrefix[], clientType?: ClientType) => {
  return Placeholders.filter(
    (placeholder) =>
      prefixes.some((prefix) => placeholder.name.startsWith(`${prefix}.`)) &&
      (clientType === undefined ||
        (clientType === ClientType.Company && !placeholder.name.startsWith('Individual.')) ||
        (clientType === ClientType.Individual && !placeholder.name.startsWith('Company.'))),
  );
};

export const templateContentToRte = (content: string | null) =>
  content
    ? content.replaceAll(PLACEHOLDER_REGEX, '<span contenteditable="false" class="e-mention-chip">$1</span>')
    : content;

export const templateContentFromRte = (content: string | null) =>
  content ? content.replaceAll(PLACEHOLDER_RTE_REGEX, '$1') : content;
